import { Badge, Group, Select, Stack, Tabs, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { DataTable } from "../../components/DataTable";
import { ReportButton } from "../../components/ReportButton";
import { fmtNum, KpiGrid, PageHeader, SEASONS } from "../../components/ui";
import { useT } from "../../i18n/script";
import { TraceDrawer } from "./LotsTab";

interface Section { kind: string; title?: string; rows: Record<string, unknown>[]; columns: { key: string; label: string; type: string }[];
  items: { label: string; value: string | number }[] }

export default function DiaryOverviewPage() {
  const { t } = useT();
  const nav = useNavigate();
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [trace, setTrace] = useState<number | null>(null);
  const sum = useQuery({ queryKey: ["diary", "summary", season], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/diary/summary", { season }) });
  const lots = useQuery({ queryKey: ["diary", "lots-all", season], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/diary/lots", { season }) });
  const comp = useQuery({ queryKey: ["diary", "uskladjenost", season],
    queryFn: () => http.post<{ sections: Section[] }>("/api/v1/reports/templates/uskladjenost_evidencija/run", { format: "json", params: { sezona: Number(season) } }) });
  const kg = (lots.data ?? []).reduce((s, l) => s + Number(l.quantity_kg), 0);
  const sold = (lots.data ?? []).reduce((s, l) => s + Number(l.sold_kg), 0);
  const phiBad = (lots.data ?? []).filter((l) => l.phi_ok === false).length;
  const compKpi = comp.data?.sections.find((s) => s.kind === "kpi")?.items ?? [];
  const compTable = comp.data?.sections.find((s) => s.kind === "table");
  return (
    <Stack>
      <PageHeader title="Poljoprivredna knjiga" description="Evidencija radova, zaštite bilja (BiH 101/12), đubrenja, navodnjavanja, berbe i prodaje za sve voćnjake u opsegu; sljedivost partija."
        right={<>
          <Select w={100} data={SEASONS} value={season} onChange={(v) => v && setSeason(v)} />
          <ReportButton code="evidencija_ffs" params={{ sezona: Number(season) }} label="Evidencija FFS" />
          <ReportButton code="berba_partije" params={{ sezona: Number(season) }} label="Berba i partije" />
        </>} />
      <KpiGrid items={[
        { label: "Voćnjaka", value: sum.data?.length ?? "—" },
        { label: "Tretmana FFS", value: fmtNum((sum.data ?? []).reduce((s, r) => s + Number(r.tretmana), 0), 0) },
        { label: "Partija / ubrano", value: `${lots.data?.length ?? 0}`, unit: `· ${fmtNum(kg / 1000, 1)} t` },
        { label: "Prodato", value: fmtNum(sold / 1000, 1), unit: "t", hint: phiBad ? <Text span c="red" size="xs">{phiBad} {t("partija prije isteka karence")}</Text> : undefined },
      ]} />
      <Tabs defaultValue="vocnjaci">
        <Tabs.List>
          <Tabs.Tab value="vocnjaci">{t("Po voćnjacima")}</Tabs.Tab>
          <Tabs.Tab value="partije">{t("Partije i sljedivost")}</Tabs.Tab>
          <Tabs.Tab value="uskladjenost" rightSection={compKpi[1] ? <Badge size="xs" color="orange">{compKpi[1].value}</Badge> : null}>{t("Usklađenost evidencija")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="vocnjaci" pt="md">
          <DataTable rows={sum.data ?? []} exportName={`knjiga_${season}`} onRowClick={(r) => nav(`/vocnjaci/${r.id}`)} columns={[
            { key: "name", label: "Voćnjak" }, { key: "species", label: "Vrsta" }, { key: "producer", label: "Proizvođač" }, { key: "opstina", label: "Opština" },
            { key: "tretmana", label: "Tretmana", type: "integer" }, { key: "dubrenja", label: "Đubrenja", type: "integer" },
            { key: "navodnjavanja", label: "Navodnjavanja", type: "integer" }, { key: "troskovi_km", label: "Troškovi (KM)", type: "money" },
            { key: "posljednji_upis", label: "Posljednji upis", type: "date" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="partije" pt="md">
          <DataTable rows={lots.data ?? []} exportName={`partije_${season}`} onRowClick={(r) => setTrace(Number(r.id))} columns={[
            { key: "lot_code", label: "Partija" }, { key: "harvest_date", label: "Berba", type: "date" }, { key: "orchard", label: "Voćnjak" },
            { key: "producer", label: "Proizvođač" }, { key: "opstina", label: "Opština" }, { key: "species", label: "Vrsta" },
            { key: "quality_class", label: "Klasa" }, { key: "quantity_kg", label: "Ubrano (kg)", type: "number", decimals: 0 },
            { key: "sold_kg", label: "Prodato (kg)", type: "number", decimals: 0 },
            { key: "phi_ok", label: "Karenca", render: (r) => <Badge size="sm" color={r.phi_ok === false ? "red" : "green"}>{r.phi_ok === false ? "NE" : "OK"}</Badge> },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="uskladjenost" pt="md">
          <Group mb="xs"><Text size="sm" c="dimmed">{t("Voćnjaci bez obaveznih upisa, sa nepotpunim podacima o tretmanu, upisima poslije 30 dana ili berbom prije isteka karence.")}</Text>
            <ReportButton code="uskladjenost_evidencija" params={{ sezona: Number(season) }} label="PDF" size="xs" /></Group>
          <DataTable rows={compTable?.rows ?? []} exportName={`uskladjenost_${season}`} columns={(compTable?.columns ?? []).map((c) => ({ key: c.key, label: c.label, type: c.type as "text" }))} />
        </Tabs.Panel>
      </Tabs>
      {trace && <TraceDrawer lotId={trace} onClose={() => setTrace(null)} />}
    </Stack>
  );
}
